import AddIcon from '@mui/icons-material/Add';
import ConstructionIcon from '@mui/icons-material/Construction';
import CropFreeIcon from '@mui/icons-material/CropFree';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MouseIcon from '@mui/icons-material/Mouse';
import RemoveIcon from '@mui/icons-material/Remove';
import { Collapse, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  zoomIn: () => void;
  zoomOut: () => void;
  fitView: () => void;
  zoomMode: boolean;
  handleZoomModeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const FlowToolbar: React.FC<Props> = ({ zoomIn, zoomOut, fitView, zoomMode, handleZoomModeChange }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className={classes.flowTools}>
      <Stack className={cx(classes.accordionHeader, { open: isOpen })} direction={'row'} justifyContent={'space-between'} onClick={handleOpen}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <ConstructionIcon className={classes.icon} />
          <Typography className={classes.headerLabel}>{t('flowTools')}</Typography>
        </Stack>
        <ExpandLessIcon className={cx(classes.expandIcon, { open: isOpen })} />
      </Stack>
      <Collapse unmountOnExit={true} in={isOpen} timeout={0}>
        <Stack>
          <Stack className={classes.buttonContainer} gap={1}>
            <PaiButton text={t('zoomIn')} startIcon={<AddIcon />} size={'small'} border={'rounded'} action={() => zoomIn()} />
            <PaiButton text={t('zoomOut')} startIcon={<RemoveIcon />} size={'small'} border={'rounded'} action={() => zoomOut()} />
            <PaiButton text={t('centerFlow')} startIcon={<CropFreeIcon />} size={'small'} border={'rounded'} action={() => fitView()} />
          </Stack>
          <Stack direction={'row'} alignItems={'center'} gap={1} sx={{ padding: '12px 16px 0 16px' }}>
            <MouseIcon className={classes.mouseIcon} />
            <Typography className={classes.caption}>{t('mouseWheelFunctionality')}</Typography>
          </Stack>
          <Stack className={classes.switchContainer} direction={'row'} alignItems={'center'} gap={2}>
            <Typography className={cx(classes.switchLabel, { active: !zoomMode })}>{t('scrollMode')}</Typography>
            <PaiSwitch checked={zoomMode} onChange={handleZoomModeChange} />
            <Typography className={cx(classes.switchLabel, { active: zoomMode })}>{t('zoomMode')}</Typography>
          </Stack>
        </Stack>
      </Collapse>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  flowTools: {
    width: '250px',
    backgroundColor: '#FFF',
    borderRadius: '10px',
    boxShadow: Shadows.GREY_SHADOW_MAIN,
    overflow: 'hidden',
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.text.secondary,
  },
  expandIcon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.primary.main,
    '&.open': {
      transform: 'rotate(180deg)',
    },
  },
  headerLabel: {
    fontSize: theme.typography.pxToRem(15),
  },
  switchLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    '&.active': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  accordionHeader: {
    padding: '8px 16px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  buttonContainer: {
    padding: '8px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  switchContainer: {
    padding: '8px 16px',
  },
  caption: {
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
  },
  mouseIcon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
  },
}));

export default FlowToolbar;
